import React, { useCallback, useEffect, useMemo, useState } from 'react';
import apiClient from '../utils/apiClient';
import { ToastContainer, ToastType } from '../components/Toast';
import AttributeLabel from '../components/AttributeLabel';
import { ModalShell } from '../components/modals/core';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  ShieldCheckIcon,
  TrashIcon,
  UserGroupIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';

type ToastItem = { id: string; type: ToastType; message: string };
type ReviewFilter = 'checked' | 'confirmed' | 'flagged';

interface Member {
  mg_nr: number;
  vorname: string | null;
  name: string | null;
  email: string | null;
}

interface Assignment {
  id: number;
  member_id: number;
  year: number;
  status: string;
  assigned_at: string | null;
  completed_at: string | null;
  notified_at: string | null;
  vorname: string | null;
  name: string | null;
  email: string | null;
  total_bookings: number;
  checked_count: number;
  flagged_count: number;
}

interface ReviewCategory {
  id: number | null;
  name: string;
  betrag: number;
}

interface ReviewTransaction {
  id: number;
  buchungstag: string;
  name_zahlungsbeteiligter: string | null;
  buchungstext: string | null;
  verwendungszweck: string | null;
  betrag: number | string;
  konto_name: string | null;
  kategorien: ReviewCategory[];
  check_status: 'confirmed' | 'flagged' | null;
  check_note: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  flagged_by_name: string | null;
  flagged_at: string | null;
}

interface AssignmentGroup {
  year: number;
  assignments: Assignment[];
  totalBookings: number;
  checkedCount: number;
  flaggedCount: number;
  confirmedCount: number;
}

const cardClass = 'rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-[#1c2432]';
const fmtDate = (value: string | null) => (value ? new Date(value).toLocaleDateString('de-DE') : '—');
const fmtDateTime = (value: string | null) => (value
  ? new Date(value).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })
  : '—');
const fmtMoney = (value: number | string) => Number(value || 0).toLocaleString('de-DE', {
  style: 'currency',
  currency: 'EUR',
});
const fullName = (assignment: Assignment) => `${assignment.vorname || ''} ${assignment.name || ''}`.trim() || 'Unbekannt';

const AdminKassenpruefer: React.FC = () => {
  const nowYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(nowYear - 1);
  const [members, setMembers] = useState<Member[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [reviewYear, setReviewYear] = useState<number | null>(null);
  const [reviewTransactions, setReviewTransactions] = useState<ReviewTransaction[]>([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>('checked');

  const addToast = useCallback((type: ToastType, message: string) => {
    const id = Date.now().toString() + Math.random();
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);
  const removeToast = (id: string) => setToasts(prev => prev.filter(t => t.id !== id));

  const loadAssignments = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/kassenpruefung/admin/assignments');
      if (res.data.success) setAssignments(res.data.assignments || []);
    } catch (e) {
      addToast('error', 'Zuweisungen konnten nicht geladen werden');
    }
  }, [addToast]);

  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get('/api/kassenpruefung/admin/members');
        if (res.data.success) setMembers(res.data.members || []);
      } catch (e) {
        addToast('error', 'Mitglieder konnten nicht geladen werden');
      }
    })();
    loadAssignments();
  }, [addToast, loadAssignments]);

  const groups = useMemo<AssignmentGroup[]>(() => {
    const byYear = new Map<number, Assignment[]>();
    assignments.forEach(assignment => {
      const entries = byYear.get(assignment.year) || [];
      entries.push(assignment);
      byYear.set(assignment.year, entries);
    });
    return Array.from(byYear.entries())
      .map(([groupYear, entries]) => {
        const first = entries[0];
        const checkedCount = Number(first?.checked_count || 0);
        const flaggedCount = Number(first?.flagged_count || 0);
        return {
          year: groupYear,
          assignments: entries,
          totalBookings: Number(first?.total_bookings || 0),
          checkedCount,
          flaggedCount,
          confirmedCount: Math.max(checkedCount - flaggedCount, 0),
        };
      })
      .sort((a, b) => b.year - a.year);
  }, [assignments]);

  const toggleMember = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const assign = async () => {
    if (selected.size === 0) {
      addToast('warning', 'Bitte mindestens ein Mitglied auswählen');
      return;
    }
    setBusy(true);
    try {
      const res = await apiClient.post('/api/kassenpruefung/admin/assignments', {
        year,
        member_ids: Array.from(selected),
      });
      if (res.data.success) {
        const newCount = (res.data.created || []).filter((created: { skipped?: boolean }) => !created.skipped).length;
        addToast('success', `${newCount} Kassenprüfer für ${year} benannt (E-Mail versandt, ${res.data.documents_granted} Belege freigegeben)`);
        setSelected(new Set());
        await loadAssignments();
      }
    } catch (e) {
      addToast('error', 'Benennung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const removeAssignment = async (assignment: Assignment) => {
    if (!window.confirm(`Zuweisung von ${fullName(assignment)} für ${assignment.year} entfernen? Der Beleg-Zugriff wird widerrufen.`)) return;
    try {
      await apiClient.delete(`/api/kassenpruefung/admin/assignments/${assignment.id}`);
      addToast('success', 'Zuweisung entfernt');
      await loadAssignments();
    } catch (e) {
      addToast('error', 'Entfernen fehlgeschlagen');
    }
  };

  const reopen = async (assignment: Assignment) => {
    try {
      await apiClient.post(`/api/kassenpruefung/admin/reopen/${assignment.id}`, {});
      addToast('success', `Prüfung für ${fullName(assignment)} wieder geöffnet`);
      await loadAssignments();
    } catch (e) {
      addToast('error', 'Wieder öffnen fehlgeschlagen');
    }
  };

  const downloadProtocol = async (assignment: Assignment) => {
    try {
      const res = await apiClient.get(`/api/kassenpruefung/admin/protocol-pdf/${assignment.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const el = document.createElement('a');
      el.href = url;
      el.download = `Kassenpruefprotokoll_${assignment.year}_${assignment.name || ''}.pdf`;
      document.body.appendChild(el);
      el.click();
      el.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      addToast('error', 'Protokoll noch nicht verfügbar (Prüfung ggf. nicht abgeschlossen)');
    }
  };

  const openReviewedBookings = async (selectedYear: number) => {
    setReviewYear(selectedYear);
    setReviewFilter('checked');
    setReviewTransactions([]);
    setReviewLoading(true);
    try {
      const res = await apiClient.get(`/api/kassenpruefung/${selectedYear}/transactions`);
      if (res.data.success) {
        const reviewed = (res.data.transactions || []).filter((transaction: ReviewTransaction) => transaction.check_status !== null);
        setReviewTransactions(reviewed);
      }
    } catch (e) {
      addToast('error', 'Geprüfte Buchungen konnten nicht geladen werden');
      setReviewYear(null);
    } finally {
      setReviewLoading(false);
    }
  };

  const filteredMembers = members.filter(member => {
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return `${member.vorname || ''} ${member.name || ''}`.toLowerCase().includes(query);
  });

  const years = Array.from(new Set([nowYear, nowYear - 1, nowYear - 2, ...assignments.map(assignment => assignment.year)]))
    .sort((a, b) => b - a);
  const yearAssignments = assignments.filter(assignment => assignment.year === year);
  const confirmedReviewCount = reviewTransactions.filter(transaction => transaction.check_status === 'confirmed').length;
  const flaggedReviewCount = reviewTransactions.filter(transaction => transaction.check_status === 'flagged').length;
  const visibleReviewTransactions = reviewTransactions.filter(transaction => (
    reviewFilter === 'checked' || transaction.check_status === reviewFilter
  ));

  return (
    <div className="space-y-6">
      <header className="app-page-header border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-primary-50 p-2 text-primary-600 dark:bg-primary-900/25 dark:text-primary-400">
            <ShieldCheckIcon className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Kassenprüfungen verwalten</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Prüfer eines Jahres arbeiten als Team und sehen denselben Prüfstatus.
            </p>
          </div>
        </div>
      </header>

      <section className={`${cardClass} p-4 sm:p-5`}>
        <div className="mb-4 flex items-center gap-2">
          <UserPlusIcon className="h-5 w-5 text-primary-600" />
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">Prüfer zum Jahres-Team hinzufügen</h2>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <div className="mb-1">
              <AttributeLabel iconKey="year" textClassName="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Prüfjahr</AttributeLabel>
            </div>
            <select
              value={year}
              onChange={event => setYear(parseInt(event.target.value, 10))}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 dark:border-gray-700 dark:bg-[#111827] dark:text-gray-100"
            >
              {years.map(optionYear => <option key={optionYear} value={optionYear}>{optionYear}</option>)}
            </select>
          </div>
          <div className="min-w-[200px] flex-1">
            <div className="mb-1">
              <AttributeLabel iconKey="search" textClassName="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Mitglied suchen</AttributeLabel>
            </div>
            <input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Name…"
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 dark:border-gray-700 dark:bg-[#111827] dark:text-gray-100"
            />
          </div>
          <button
            onClick={assign}
            disabled={busy || selected.size === 0}
            className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-40"
          >
            <UserPlusIcon className="h-4 w-4" />
            {selected.size > 0 ? `${selected.size} hinzufügen` : 'Hinzufügen'}
          </button>
        </div>

        <div className="mt-4 max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-xl border border-gray-200 dark:divide-gray-800 dark:border-gray-700">
          {filteredMembers.map(member => {
            const alreadyAssigned = yearAssignments.some(assignment => assignment.member_id === member.mg_nr);
            return (
              <label
                key={member.mg_nr}
                className={`flex min-h-11 cursor-pointer items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 ${alreadyAssigned ? 'opacity-50' : ''}`}
              >
                <input
                  type="checkbox"
                  disabled={alreadyAssigned}
                  checked={selected.has(member.mg_nr)}
                  onChange={() => toggleMember(member.mg_nr)}
                  className="h-4 w-4 rounded text-primary-600 focus:ring-primary-500"
                />
                <span className="flex-1 text-sm text-gray-800 dark:text-gray-100">{member.vorname} {member.name}</span>
                {!member.email && <span className="text-xs text-amber-600 dark:text-amber-400">keine E-Mail</span>}
                {alreadyAssigned && <span className="text-xs text-gray-400">im Team</span>}
              </label>
            );
          })}
          {filteredMembers.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-gray-400">Keine Mitglieder gefunden</div>
          )}
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between px-1">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Prüfteams nach Jahr</h2>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">Fortschritt und Entscheidungen gelten gemeinsam für das gesamte Jahres-Team.</p>
          </div>
          <span className="rounded-full border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-500 dark:border-gray-700 dark:text-gray-400">
            {groups.length} {groups.length === 1 ? 'Prüfjahr' : 'Prüfjahre'}
          </span>
        </div>

        {groups.map(group => {
          const progress = group.totalBookings > 0 ? Math.round((group.checkedCount / group.totalBookings) * 100) : 0;
          const completedMembers = group.assignments.filter(assignment => assignment.status === 'completed').length;
          const allCompleted = completedMembers === group.assignments.length;
          return (
            <article key={group.year} className={`${cardClass} overflow-hidden`}>
              <div className="border-b border-gray-200 p-4 dark:border-gray-700 sm:p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="min-w-16 rounded-xl border border-primary-200 bg-primary-50 px-3 py-2 text-center dark:border-primary-800 dark:bg-primary-900/20">
                      <div className="text-lg font-bold text-primary-700 dark:text-primary-300">{group.year}</div>
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-400">Prüfjahr</div>
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Gemeinsames Prüfteam</h3>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${allCompleted ? 'bg-green-100 text-green-700 dark:bg-green-900/25 dark:text-green-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300'}`}>
                          {allCompleted ? 'Team abgeschlossen' : `${completedMembers}/${group.assignments.length} abgeschlossen`}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {group.assignments.map(assignment => (
                          <span key={assignment.id} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-[10px] font-bold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                              {(assignment.vorname?.[0] || assignment.name?.[0] || '?').toUpperCase()}
                            </span>
                            {fullName(assignment)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => openReviewedBookings(group.year)}
                    disabled={group.checkedCount === 0}
                    className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300 dark:hover:bg-primary-900/35 dark:disabled:border-gray-700 dark:disabled:bg-gray-800 dark:disabled:text-gray-500"
                  >
                    <EyeIcon className="h-4 w-4" />
                    {group.checkedCount > 0 ? `${group.checkedCount} geprüfte Buchungen ansehen` : 'Noch nichts geprüft'}
                  </button>
                </div>

                <div className="mt-5 grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
                  <div>
                    <div className="mb-2 flex items-center justify-between text-xs text-gray-600 dark:text-gray-300">
                      <span>{group.checkedCount} von {group.totalBookings} Buchungen geprüft</span>
                      <span className="font-semibold">{progress}%</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                      <div className="h-full rounded-full bg-primary-600 transition-all" style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                  <div className="flex gap-2 text-xs">
                    <span className="inline-flex items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-2.5 py-1.5 font-medium text-green-700 dark:border-green-900/60 dark:bg-green-900/20 dark:text-green-300">
                      <CheckCircleIcon className="h-4 w-4" /> {group.confirmedCount} genehmigt
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 font-medium text-amber-700 dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-300">
                      <ExclamationTriangleIcon className="h-4 w-4" /> {group.flaggedCount} beanstandet
                    </span>
                  </div>
                </div>
              </div>

              <div className="divide-y divide-gray-100 dark:divide-gray-800">
                {group.assignments.map(assignment => (
                  <div key={assignment.id} className="flex flex-col gap-3 px-4 py-3 sm:px-5 md:flex-row md:items-center">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-gray-50 text-xs font-bold text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                        {(assignment.vorname?.[0] || '')}{(assignment.name?.[0] || '')}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{fullName(assignment)}</div>
                        <div className="truncate text-xs text-gray-500 dark:text-gray-400">{assignment.email || 'Keine E-Mail hinterlegt'}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 md:justify-end">
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                        <EnvelopeIcon className="h-4 w-4" /> {assignment.notified_at ? `Benachrichtigt ${fmtDate(assignment.notified_at)}` : 'Nicht benachrichtigt'}
                      </span>
                      {assignment.status === 'completed' ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                          <CheckCircleIcon className="h-4 w-4" /> Abgeschlossen {fmtDate(assignment.completed_at)}
                        </span>
                      ) : (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/25 dark:text-amber-300">Offen</span>
                      )}
                      <div className="ml-auto flex items-center gap-1 md:ml-1">
                        {assignment.status === 'completed' && (
                          <>
                            <button
                              onClick={() => downloadProtocol(assignment)}
                              title={`Protokoll von ${fullName(assignment)} herunterladen`}
                              className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700"
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => reopen(assignment)}
                              title={`Prüfung für ${fullName(assignment)} wieder öffnen`}
                              className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700"
                            >
                              <ArrowPathIcon className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => removeAssignment(assignment)}
                          title={`${fullName(assignment)} aus dem Prüfteam entfernen`}
                          className="rounded-lg border border-gray-200 p-2 text-red-500 hover:bg-red-50 dark:border-gray-700 dark:hover:bg-red-900/20"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </article>
          );
        })}

        {groups.length === 0 && (
          <div className={`${cardClass} px-4 py-12 text-center`}>
            <UserGroupIcon className="mx-auto h-9 w-9 text-gray-300 dark:text-gray-600" />
            <p className="mt-3 text-sm font-medium text-gray-600 dark:text-gray-300">Noch keine Prüfteams angelegt</p>
          </div>
        )}
      </section>

      <ModalShell
        open={reviewYear !== null}
        onClose={() => setReviewYear(null)}
        variant="form"
        size="xl"
        mobilePresentation="fullscreen"
        title={`Geprüfte Buchungen ${reviewYear || ''}`}
        subtitle="Gemeinsamer Prüfstand aller Kassenprüfer dieses Jahres"
        icon={EyeIcon}
        bodyClassName="p-0"
      >
        <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700 sm:px-5">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Prüfstatus filtern">
            {([
              ['checked', 'Alle geprüft', reviewTransactions.length],
              ['confirmed', 'Genehmigt', confirmedReviewCount],
              ['flagged', 'Beanstandet', flaggedReviewCount],
            ] as const).map(([filter, label, count]) => (
              <button
                key={filter}
                type="button"
                role="tab"
                aria-selected={reviewFilter === filter}
                onClick={() => setReviewFilter(filter)}
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${reviewFilter === filter
                  ? 'border-primary-600 bg-primary-600 text-white'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`}
              >
                {label} <span className="ml-1 opacity-75">{count}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="max-h-[calc(100dvh-180px)] overflow-y-auto sm:max-h-[64vh]">
          {reviewLoading ? (
            <div className="flex min-h-56 items-center justify-center gap-3 text-sm text-gray-500 dark:text-gray-400">
              <ArrowPathIcon className="h-5 w-5 animate-spin" /> Buchungen werden geladen…
            </div>
          ) : visibleReviewTransactions.length === 0 ? (
            <div className="px-4 py-14 text-center text-sm text-gray-500 dark:text-gray-400">
              Für diesen Filter sind keine geprüften Buchungen vorhanden.
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {visibleReviewTransactions.map(transaction => {
                const confirmed = transaction.check_status === 'confirmed';
                const description = transaction.name_zahlungsbeteiligter || transaction.buchungstext || 'Buchung';
                const purpose = transaction.verwendungszweck || transaction.buchungstext;
                return (
                  <article key={transaction.id} className="px-4 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/35 sm:px-5">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{fmtDate(transaction.buchungstag)}</span>
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${confirmed
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/25 dark:text-green-300'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/25 dark:text-amber-300'}`}
                          >
                            {confirmed ? <CheckCircleIcon className="h-3.5 w-3.5" /> : <ExclamationTriangleIcon className="h-3.5 w-3.5" />}
                            {confirmed ? 'Genehmigt' : 'Beanstandet'}
                          </span>
                        </div>
                        <h3 className="mt-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">{description}</h3>
                        {purpose && purpose !== description && (
                          <p className="mt-0.5 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">{purpose}</p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {(transaction.kategorien || []).map((category, index) => (
                            <span key={`${category.id ?? 'none'}-${index}`} className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                              {category.name} · {fmtMoney(category.betrag)}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="shrink-0 sm:text-right">
                        <div className={`text-base font-bold ${Number(transaction.betrag) < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                          {fmtMoney(transaction.betrag)}
                        </div>
                        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{transaction.konto_name || 'Konto unbekannt'}</div>
                      </div>
                    </div>
                    <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${confirmed
                      ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-900/60 dark:bg-green-900/25 dark:text-green-300'
                      : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-900/25 dark:text-amber-300'}`}
                    >
                      {confirmed
                        ? `${transaction.approved_by_name || 'Ein Kassenprüfer'} hat am ${fmtDateTime(transaction.approved_at)} genehmigt.`
                        : `${transaction.flagged_by_name || 'Ein Kassenprüfer'} hat am ${fmtDateTime(transaction.flagged_at)} beanstandet${transaction.check_note ? `: ${transaction.check_note}` : '.'}`}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </ModalShell>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default AdminKassenpruefer;
